import { useState } from 'react';
import type { Preventivo } from '../../types';
import { formatCurrency } from '../../utils/pricing';

interface PreventivoDetailsProps {
  preventivo: Preventivo;
  onUpdate: (data: Partial<Preventivo>) => void;
  onClose: () => void;
  isLoading?: boolean;
}

export function PreventivoDetails({
  preventivo,
  onUpdate,
  onClose,
  isLoading,
}: PreventivoDetailsProps) {
  const [formData, setFormData] = useState({
    categoria: preventivo.categoria,
    voce: preventivo.voce,
    costoMin: preventivo.costoMin,
    costoMed: preventivo.costoMed,
    costoMax: preventivo.costoMax,
    note: preventivo.note || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onUpdate({
      ...formData,
      note: formData.note || null,
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const categorie = [
    'Infissi',
    'Impianti',
    'Finiture',
    'Muratura',
    'Esterni',
    'Arredamento',
    'Altro',
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-auto animate-slide-up">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">
              Modifica Preventivo
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="input-group">
              <label htmlFor="categoria" className="input-label">
                Categoria
              </label>
              <select
                id="categoria"
                name="categoria"
                value={formData.categoria}
                onChange={handleChange}
                className="w-full"
                required
              >
                {categorie.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <div className="input-group">
              <label htmlFor="voce" className="input-label">
                Descrizione Voce
              </label>
              <input
                type="text"
                id="voce"
                name="voce"
                value={formData.voce}
                onChange={handleChange}
                className="w-full"
                required
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="input-group">
                <label htmlFor="costoMin" className="input-label">
                  Costo Min (€)
                </label>
                <input
                  type="number"
                  id="costoMin"
                  name="costoMin"
                  value={formData.costoMin}
                  onChange={handleChange}
                  className="w-full"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <div className="input-group">
                <label htmlFor="costoMed" className="input-label">
                  Costo Med (€)
                </label>
                <input
                  type="number"
                  id="costoMed"
                  name="costoMed"
                  value={formData.costoMed}
                  onChange={handleChange}
                  className="w-full"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <div className="input-group">
                <label htmlFor="costoMax" className="input-label">
                  Costo Max (€)
                </label>
                <input
                  type="number"
                  id="costoMax"
                  name="costoMax"
                  value={formData.costoMax}
                  onChange={handleChange}
                  className="w-full"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
            </div>

            {/* Summary */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-xs text-gray-500">Minimo</p>
                  <p className="font-semibold text-gray-900 currency">
                    {formatCurrency(parseFloat(formData.costoMin) || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Medio</p>
                  <p className="font-semibold text-primary-600 currency">
                    {formatCurrency(parseFloat(formData.costoMed) || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Massimo</p>
                  <p className="font-semibold text-gray-900 currency">
                    {formatCurrency(parseFloat(formData.costoMax) || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="note" className="input-label">
                Note (opzionale)
              </label>
              <textarea
                id="note"
                name="note"
                value={formData.note}
                onChange={handleChange}
                className="w-full"
                rows={3}
                placeholder="Aggiungi eventuali note..."
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary flex-1"
                disabled={isLoading}
              >
                Annulla
              </button>
              <button
                type="submit"
                className="btn-primary flex-1"
                disabled={isLoading}
              >
                {isLoading ? 'Salvataggio...' : 'Salva Modifiche'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
